import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { AdminLayout } from "@/pages/AdminDashboard";
import api from "@/services/api";
import { toast } from "sonner";
import { SITE_LOGO } from "@/constants/assets";
import {
  ArrowLeft,
  Download,
  Phone,
  Calendar,
  Users,
  DollarSign,
  Heart,
  Sparkles,
  MessageSquare,
  Send,
  MapPin,
  Palette,
  Building,
  Briefcase,
  Pencil,
  Trash2,
  Save,
  X
} from "lucide-react";
import jsPDF from "jspdf";

const STATUSES = [
  { value: "new", label: "New", color: "#60a5fa" },
  { value: "contacted", label: "Contacted", color: "#fbbf24" },
  { value: "qualified", label: "Qualified", color: "#4ade80" },
  { value: "closed", label: "Closed", color: "#9ca3af" }
];

export default function AdminSubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const isEditing = searchParams.get("edit") === "1";

  const fetchSubmission = useCallback(async () => {
    if (!token || !id) return;
    try {
      const response = await api.getSubmission(id, token);
      console.log("Submission data:", response);
      if (response.error) {
        toast.error("Failed to fetch submission");
        navigate("/admin/submissions");
      } else {
        setSubmission(response);
      }
    } catch (error) {
      toast.error("Failed to fetch submission");
      navigate("/admin/submissions");
    } finally {
      setLoading(false);
    }
  }, [token, id, navigate]);

  const fetchNotes = useCallback(async () => {
    if (!token || !id) return;
    try {
      const response = await api.getNotes(id, token);
      if (!response.error) {
        setNotes(response.notes || []);
      }
    } catch (error) {
      console.error("Failed to fetch notes");
    }
  }, [token, id]);

  useEffect(() => {
    fetchSubmission();
    fetchNotes();
  }, [fetchSubmission, fetchNotes]);

  useEffect(() => {
    if (submission && isEditing) {
      setEditForm({
        couple_names: submission.couple_names || "",
        phone: submission.phone || "",
        country_code: submission.country_code || "91",
        wedding_date: submission.wedding_date || "",
        guest_count: submission.guest_count || "",
        wedding_destination: submission.wedding_destination || "",
        wedding_destination_other: submission.wedding_destination_other || "",
        budget_range: submission.budget_range || "",
        status: submission.status || "new",
        styling_preferences: submission.styling_preferences || "",
        ritual_details: submission.ritual_details || "",
      });
    } else {
      setEditForm(null);
    }
  }, [submission, isEditing]);

  const startEditing = () => setSearchParams({ edit: "1" });
  const cancelEditing = () => setSearchParams({});

  const saveEdits = async (e) => {
    e.preventDefault();
    if (!editForm) return;
    setSaving(true);
    try {
      const response = await api.updateSubmission(
        { id, ...editForm, guest_count: Number(editForm.guest_count) || 0 },
        token
      );
      if (response.error) {
        toast.error(response.error);
      } else {
        setSubmission(response.submission || { ...submission, ...editForm });
        toast.success("Submission updated");
        setSearchParams({});
      }
    } catch {
      toast.error("Failed to update submission");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${submission?.reference_id}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const response = await api.deleteSubmission(id, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success("Submission deleted");
        navigate("/admin/submissions");
      }
    } catch {
      toast.error("Failed to delete submission");
    } finally {
      setDeleting(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await api.updateSubmissionStatus(id, newStatus, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        setSubmission({ ...submission, status: newStatus });
        toast.success("Status updated");
      }
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      const response = await api.addNote(id, newNote, token);
      if (response.error) {
        toast.error(response.error);
      } else {
        setNotes([response.note, ...notes]);
        setNewNote("");
        toast.success("Note added");
      }
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  // Helper functions to parse data
  const parseArrayField = (field) => {
    if (Array.isArray(field)) return field;
    if (typeof field === 'string' && field.trim()) {
      return field.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  const parseServiceDetails = (details) => {
    if (typeof details === 'object' && details !== null && !Array.isArray(details)) {
      return details;
    }
    if (typeof details === 'string') {
      try {
        return JSON.parse(details);
      } catch {
        return {};
      }
    }
    return {};
  };

  const generatePDF = async () => {
    if (!submission) return;
    setGeneratingPdf(true);
    try {
      const nuptialEvents     = parseArrayField(submission.nuptial_events);
      const mainServices      = parseArrayField(submission.main_services);
      const serviceDetails    = parseServiceDetails(submission.service_details);
      const stylingThemes     = parseArrayField(submission.styling_themes);
      const propertyRoomSplit = parseArrayField(submission.property_room_split);

      const weddingDate = submission.wedding_date
        ? new Date(submission.wedding_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
        : '-';

      // ── PDF Setup ──────────────────────────────────────────
      const pdf   = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const PW    = 210;
      const PH    = 297;
      const ML    = 18;
      const MR    = 18;
      const CW    = PW - ML - MR;

      // Colour palette
      const ACCENT  = [196, 100, 106];
      const ACCENT2 = [220, 155, 160];
      const DARK    = [22,  22,  22];
      const MID     = [60,  60,  60];
      const GRAY    = [110, 110, 110];
      const LGRAY   = [210, 210, 210];
      const WHITE   = [255, 255, 255];
      const GREEN   = [22,  163, 74];
      const PURPLE  = [109, 40,  217];
      const BGLIGHT = [255, 250, 250];
      const BGCARD  = [252, 246, 246];
      const BGSTRIP = [254, 248, 248];

      let y = 0;
      let pageNum = 1;

      // ── Page helpers ───────────────────────────────────────
      const addPageHeader = () => {
        pdf.setFillColor(...ACCENT);
        pdf.rect(0, 0, PW, 3.5, 'F');
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.5);
        pdf.setTextColor(...GRAY);
        pdf.text(`Page ${pageNum}`, PW - MR, PH - 6, { align: 'right' });
        pdf.setDrawColor(...LGRAY);
        pdf.setLineWidth(0.25);
        pdf.line(ML, PH - 11, ML + CW, PH - 11);
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(8);
        pdf.setTextColor(...GRAY);
        pdf.text('Inventive Pages — Carving Celebrations, Creating Memories!', ML, PH - 6);
      };

      const newPage = () => {
        addPageHeader();
        pdf.addPage();
        pageNum++;
        y = 16;
        pdf.setFillColor(...ACCENT);
        pdf.rect(0, 0, PW, 3.5, 'F');
      };

      const checkY = (needed = 14) => {
        if (y + needed > PH - 20) newPage();
      };

      const sf = (style = 'normal', size = 12) => {
        pdf.setFont('helvetica', style);
        pdf.setFontSize(size);
      };
      const sc = (rgb) => pdf.setTextColor(...rgb);

      // ── Section heading ────────────────────────────────────
      const sectionHeading = (num, title) => {
        checkY(26);
        y += 6;
        // Full-width background band
        pdf.setFillColor(...BGSTRIP);
        pdf.rect(0, y - 6, PW, 17, 'F');
        // Left accent bar (full height)
        pdf.setFillColor(...ACCENT);
        pdf.rect(0, y - 6, 5, 17, 'F');
        // Number badge circle
        pdf.setFillColor(...ACCENT);
        pdf.circle(ML + 9, y + 2, 5.5, 'F');
        sf('bold', 10); sc(WHITE);
        pdf.text(String(num), ML + 9, y + 2.8, { align: 'center' });
        // Title — large bold
        sf('bold', 16); sc(DARK);
        pdf.text(title, ML + 20, y + 4);
        y += 14;
        // Accent underline
        pdf.setDrawColor(...ACCENT);
        pdf.setLineWidth(0.6);
        pdf.line(ML, y, ML + CW, y);
        y += 7;
      };

      // ── Key-value row ──────────────────────────────────────
      const kvRow = (label, value, labelW = 68) => {
        if (!value) return;
        checkY(12);
        sf('normal', 11); sc(GRAY);
        pdf.text(label, ML, y);
        sf('bold', 13); sc(DARK);
        const lines = pdf.splitTextToSize(String(value), CW - labelW - 4);
        pdf.text(lines, ML + labelW, y);
        y += lines.length * 7.5 + 2;
      };

      // ── Body text ──────────────────────────────────────────
      const bodyText = (text, size = 13, color = MID, indent = 0) => {
        if (!text) return;
        checkY(10);
        sf('normal', size); sc(color);
        const lines = pdf.splitTextToSize(String(text), CW - indent);
        pdf.text(lines, ML + indent, y);
        y += lines.length * (size * 0.52) + 3;
      };

      // ── Pill tags ──────────────────────────────────────────
      const drawTags = (items, fillRgb = ACCENT, textRgb = WHITE) => {
        if (!items || items.length === 0) return;
        checkY(14);
        const tagH = 9; const padX = 6; const gap = 5;
        let tx = ML;
        sf('normal', 11);
        items.forEach(item => {
          const tw = pdf.getTextWidth(String(item)) + padX * 2;
          if (tx + tw > ML + CW) {
            tx = ML; y += tagH + gap + 1;
            checkY(tagH + gap);
          }
          pdf.setFillColor(...fillRgb);
          pdf.roundedRect(tx, y - tagH + 2, tw, tagH, 3.5, 3.5, 'F');
          sc(textRgb);
          pdf.text(String(item), tx + padX, y - 0.2);
          tx += tw + gap;
        });
        y += tagH + 4;
      };

      // ── Service detail card ────────────────────────────────
      const serviceCard = (name, tags) => {
        if (!tags || tags.length === 0) return;
        const rows = Math.ceil(tags.length / 3);
        const cardH = 13 + rows * 13 + 5;
        checkY(cardH + 6);
        pdf.setFillColor(250, 248, 252);
        pdf.setDrawColor(...LGRAY);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(ML, y, CW, cardH, 3.5, 3.5, 'FD');
        pdf.setFillColor(...ACCENT);
        pdf.roundedRect(ML, y, 4, cardH, 2, 2, 'F');
        y += 5;
        sf('bold', 13); sc(DARK);
        pdf.text(name, ML + 10, y + 4);
        y += 11;
        const tagH = 8; const padX = 5; const gap = 4;
        let tx = ML + 10;
        sf('normal', 10);
        tags.forEach(tag => {
          const tw = pdf.getTextWidth(String(tag)) + padX * 2;
          if (tx + tw > ML + CW - 8) { tx = ML + 10; y += tagH + gap; }
          pdf.setFillColor(232, 228, 242);
          pdf.roundedRect(tx, y - tagH + 2, tw, tagH, 2.5, 2.5, 'F');
          sc([55, 50, 90]);
          pdf.text(String(tag), tx + padX, y - 0.2);
          tx += tw + gap;
        });
        y += tagH + 7;
      };

      // ── PAGE 1 HEADER ──────────────────────────────────────
      // Full-bleed header background
      pdf.setFillColor(...BGLIGHT);
      pdf.rect(0, 0, PW, 66, 'F');
      // Top accent stripe
      pdf.setFillColor(...ACCENT);
      pdf.rect(0, 0, PW, 5, 'F');
      // Bottom accent stripe
      pdf.setFillColor(...ACCENT);
      pdf.rect(0, 65, PW, 1.5, 'F');
      // Decorative side bars
      pdf.setFillColor(...ACCENT2);
      pdf.rect(0, 5, 4, 60, 'F');
      pdf.rect(PW - 4, 5, 4, 60, 'F');

      // Logo
      try {
        const logoUrl = SITE_LOGO;
        const img = await new Promise((res, rej) => {
          const i = new Image(); i.crossOrigin = 'anonymous';
          i.onload = () => res(i); i.onerror = rej; i.src = logoUrl;
        });
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        const logoData = canvas.toDataURL('image/png');
        const logoH = 28; const logoW = (img.naturalWidth / img.naturalHeight) * logoH;
        pdf.addImage(logoData, 'PNG', (PW - logoW) / 2, 8, logoW, logoH);
      } catch (_) {
        sf('bold', 22); sc(ACCENT);
        pdf.text('INVENTIVE PAGES', PW / 2, 26, { align: 'center' });
      }

      // Title block — large decorative
      sf('bold', 30); sc(ACCENT);
      pdf.text('WEDDING BRIEF', PW / 2, 48, { align: 'center' });

      // Decorative diamond divider
      const dLineW = 60;
      pdf.setDrawColor(...ACCENT2);
      pdf.setLineWidth(0.7);
      pdf.line((PW - dLineW) / 2, 52, (PW - 8) / 2, 52);
      pdf.line((PW + 8) / 2, 52, (PW + dLineW) / 2, 52);
      // Diamond
      pdf.setFillColor(...ACCENT2);
      pdf.circle(PW / 2, 52, 1.5, 'F');

      // Reference + date
      sf('normal', 10.5); sc(GRAY);
      const genDate = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
      pdf.text(`Reference: ${submission.reference_id || '-'}   |   Generated: ${genDate}`, PW / 2, 60, { align: 'center' });

      y = 76;

      // ── SECTIONS ───────────────────────────────────────────
      let sn = 1;

      // 1. Contact Information
      sectionHeading(sn++, 'Contact Information');
      kvRow('Couple Names',   submission.couple_names || '-');
      kvRow('WhatsApp / Phone', `+${submission.country_code || '91'} ${submission.phone || '-'}`);
      y += 5;

      // 2. Desired Wedding Date
      sectionHeading(sn++, 'Desired Wedding Date');
      checkY(14);
      pdf.setFillColor(...BGCARD);
      pdf.roundedRect(ML, y - 2, CW, 14, 3, 3, 'F');
      sf('bold', 18); sc(ACCENT);
      pdf.text(weddingDate, ML + 6, y + 8);
      y += 18;

      // 3. Expected Guest Count
      sectionHeading(sn++, 'Expected Guest Count');
      checkY(14);
      pdf.setFillColor(...BGCARD);
      pdf.roundedRect(ML, y - 2, CW, 14, 3, 3, 'F');
      sf('bold', 18); sc(DARK);
      pdf.text(submission.guest_count ? `${submission.guest_count} guests` : '-', ML + 6, y + 8);
      y += 18;

      // 4. Wedding Services Required
      sectionHeading(sn++, 'Wedding Services Required');
      if (mainServices.length > 0) {
        drawTags(mainServices, GREEN, WHITE);
      } else {
        bodyText('No services selected', 13, GRAY);
      }
      y += 4;

      // 5. Service Details
      sectionHeading(sn++, 'Service Details');
      const detailEntries = Object.entries(serviceDetails).filter(([, t]) => t?.length > 0);
      if (detailEntries.length > 0) {
        detailEntries.forEach(([svc, tags]) => {
          serviceCard(svc, Array.isArray(tags) ? tags : []);
          y += 3;
        });
      } else {
        bodyText('No specific service details provided', 13, GRAY);
      }
      y += 4;

      // 6. Nuptial Events
      sectionHeading(sn++, 'Nuptial Events');
      if (nuptialEvents.length > 0) {
        drawTags(nuptialEvents, ACCENT, WHITE);
        if (submission.nuptial_events_other) {
          y += 2;
          bodyText(`Other: ${submission.nuptial_events_other}`, 12, GRAY);
        }
      } else {
        bodyText('No events selected', 13, GRAY);
      }
      y += 4;

      // 7. Preferred Destination
      sectionHeading(sn++, 'Preferred Wedding Destination');
      const destText = `${submission.wedding_destination || '-'}${submission.wedding_destination_other ? ` — ${submission.wedding_destination_other}` : ''}`;
      checkY(14);
      pdf.setFillColor(...BGCARD);
      pdf.roundedRect(ML, y - 2, CW, 14, 3, 3, 'F');
      sf('bold', 16); sc(DARK);
      pdf.text(destText, ML + 6, y + 8);
      y += 18;

      // 8. Property Requirements (conditional)
      if (submission.property_hotel_size || submission.property_count || submission.property_type) {
        sectionHeading(sn++, 'Property Requirements');
        if (submission.property_hotel_size) kvRow('Hotel Size',            `${submission.property_hotel_size} rooms`);
        if (submission.property_count)      kvRow('Number of Properties',  submission.property_count);
        if (propertyRoomSplit.length > 0)   kvRow('Room Split',            propertyRoomSplit.join(', '));
        if (submission.property_type)       kvRow('Property Type',         submission.property_type);
        if (submission.property_budget_per_night) kvRow('Budget per Room/Night', submission.property_budget_per_night);
        if (submission.property_triple_occupancy) {
          kvRow('Triple Occupancy', `${submission.property_triple_occupancy}${submission.property_triple_count ? ` (${submission.property_triple_count} rooms)` : ''}`);
        }
        if (submission.property_custom_requirement) {
          y += 3;
          sf('bold', 11); sc(GRAY); pdf.text('Custom Requirements:', ML, y); y += 7;
          bodyText(submission.property_custom_requirement, 13, DARK);
        }
        y += 4;
      }

      // 9. Wedding Vision & Themes
      sectionHeading(sn++, 'Wedding Vision & Themes');
      if (stylingThemes.length > 0) {
        drawTags(stylingThemes, PURPLE, WHITE);
        y += 2;
      }
      if (submission.styling_preferences) {
        bodyText(submission.styling_preferences, 13, DARK);
      } else if (stylingThemes.length === 0) {
        bodyText('No specific vision details provided', 13, GRAY);
      }
      y += 4;

      // 10. Rituals & Traditions
      sectionHeading(sn++, 'Rituals & Traditions');
      if (submission.has_rituals === true || submission.has_rituals === 'Yes') {
        bodyText(submission.ritual_details || 'Yes — Specific rituals to be discussed', 13, DARK);
      } else {
        bodyText('No specific rituals or traditions mentioned', 13, GRAY);
      }
      y += 4;

      // 11. Estimated Budget (highlighted box)
      sectionHeading(sn++, 'Estimated Budget');
      checkY(24);
      pdf.setFillColor(...BGCARD);
      pdf.setDrawColor(...ACCENT);
      pdf.setLineWidth(0.7);
      pdf.roundedRect(ML, y, CW, 22, 4, 4, 'FD');
      pdf.setFillColor(...ACCENT);
      pdf.roundedRect(ML, y, 5, 22, 2, 2, 'F');
      sf('normal', 12); sc(GRAY);
      pdf.text('Total Budget Range', ML + 12, y + 8);
      sf('bold', 22); sc(ACCENT);
      pdf.text(submission.budget_range || '-', ML + 12, y + 18);
      y += 28;

      // ── FOOTER on last page ────────────────────────────────
      addPageHeader();
      checkY(55);
      const footerY = PH - 52;

      // Footer background
      pdf.setFillColor(...BGLIGHT);
      pdf.rect(0, footerY - 2, PW, 54, 'F');
      pdf.setFillColor(...ACCENT);
      pdf.rect(0, footerY - 2, PW, 2.5, 'F');

      // Tagline
      sf('italic', 14); sc(ACCENT);
      pdf.text('Carving Celebrations, Creating Memories!', PW / 2, footerY + 10, { align: 'center' });

      // Divider
      pdf.setDrawColor(...LGRAY);
      pdf.setLineWidth(0.3);
      pdf.line(ML + 15, footerY + 15, ML + CW - 15, footerY + 15);

      // Contact columns
      const contacts = [
        { name: 'Aankiit Bundela', phone: '+91 98989 89863', email: 'ankitbundela@gmail.com' },
        { name: 'Hettna Bundela', phone: '+91 9898907080', email: 'hetna.13@gmail.com' },
      ];
      const colW = CW / contacts.length;
      contacts.forEach((c, i) => {
        const cx = ML + i * colW + colW / 2;
        sf('bold', 11); sc(DARK);
        pdf.text(c.name,  cx, footerY + 24, { align: 'center' });
        sf('normal', 10); sc(MID);
        pdf.text(c.phone, cx, footerY + 32, { align: 'center' });
        sf('normal', 10); sc(ACCENT);
        pdf.text(c.email, cx, footerY + 40, { align: 'center' });
      });

      sf('normal', 9); sc(GRAY);
      pdf.text('www.inventivepages.com', PW / 2, footerY + 48, { align: 'center' });

      // ── Save ───────────────────────────────────────────────
      pdf.save(`wedding-brief-${submission.reference_id}.pdf`);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const canEditStatus = user?.role === "super_admin" || user?.role === "planner";
  const canEdit = canEditStatus;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c4646a]"></div>
        </div>
      </AdminLayout>
    );
  }

  // Parse fields for display
  const nuptialEvents = parseArrayField(submission?.nuptial_events);
  const mainServices = parseArrayField(submission?.main_services);
  const serviceDetails = parseServiceDetails(submission?.service_details);
  const stylingThemes = parseArrayField(submission?.styling_themes);
  const propertyRoomSplit = parseArrayField(submission?.property_room_split);

  return (
    <AdminLayout>
      <div className="p-6 md:p-8" data-testid="submission-detail">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/submissions")}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-light text-white">{submission?.couple_names}</h1>
              <p className="text-gray-400 font-mono">{submission?.reference_id}</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            {canEdit && !isEditing && (
              <button
                onClick={startEditing}
                className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 transition-colors"
              >
                <Pencil className="w-4 h-4" />
                Edit
              </button>
            )}
            {canEdit && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-600/20 border border-red-600/40 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
            <button
              onClick={generatePDF}
              disabled={generatingPdf}
              className="flex items-center gap-2 px-4 py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {generatingPdf ? "Generating..." : "Download PDF"}
            </button>
          </div>
        </div>

        {isEditing && editForm && (
          <form onSubmit={saveEdits} className="bg-white/5 border border-[#c4646a]/30 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-white">Edit Submission</h2>
              <button type="button" onClick={cancelEditing} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <EditField label="Couple Names" value={editForm.couple_names} onChange={(v) => setEditForm({ ...editForm, couple_names: v })} />
              <EditField label="Phone" value={editForm.phone} onChange={(v) => setEditForm({ ...editForm, phone: v })} />
              <EditField label="Country Code" value={editForm.country_code} onChange={(v) => setEditForm({ ...editForm, country_code: v })} />
              <EditField label="Wedding Date" type="date" value={editForm.wedding_date} onChange={(v) => setEditForm({ ...editForm, wedding_date: v })} />
              <EditField label="Guest Count" type="number" value={editForm.guest_count} onChange={(v) => setEditForm({ ...editForm, guest_count: v })} />
              <EditField label="Destination" value={editForm.wedding_destination} onChange={(v) => setEditForm({ ...editForm, wedding_destination: v })} />
              <EditField label="Destination (Other)" value={editForm.wedding_destination_other} onChange={(v) => setEditForm({ ...editForm, wedding_destination_other: v })} />
              <div>
                <label className="block text-sm text-gray-400 mb-2">Budget Range</label>
                <select
                  value={editForm.budget_range}
                  onChange={(e) => setEditForm({ ...editForm, budget_range: e.target.value })}
                  className="luxury-input py-3 w-full"
                >
                  <option value="">Select budget</option>
                  {["Below ₹50 Lakhs", "₹50 Lakhs – ₹75 Lakhs", "₹75 Lakhs – ₹1.5 Crores", "₹1.5 CR – ₹3 CR", "₹3 CR – ₹5 CR", "₹5 Crores+"].map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="luxury-input py-3 w-full"
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-2">Styling Preferences</label>
                <textarea
                  value={editForm.styling_preferences}
                  onChange={(e) => setEditForm({ ...editForm, styling_preferences: e.target.value })}
                  className="luxury-input py-3 w-full min-h-[80px]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-2">Ritual Details</label>
                <textarea
                  value={editForm.ritual_details}
                  onChange={(e) => setEditForm({ ...editForm, ritual_details: e.target.value })}
                  className="luxury-input py-3 w-full min-h-[80px]"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" disabled={saving} className="luxury-button flex items-center gap-2 disabled:opacity-50">
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button type="button" onClick={cancelEditing} className="px-4 py-2 text-gray-400 hover:text-white">
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Info — follows form question order */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Card */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-medium text-white mb-4">Lead Status</h2>
              <div className="flex flex-wrap gap-3">
                {STATUSES.map((status) => (
                  <button
                    key={status.value}
                    onClick={() => canEditStatus && updateStatus(status.value)}
                    disabled={!canEditStatus || updating}
                    className={`px-4 py-2 rounded-lg border-2 transition-all ${
                      submission?.status === status.value
                        ? "border-current"
                        : "border-transparent bg-white/5 hover:bg-white/10"
                    } ${!canEditStatus ? "cursor-not-allowed opacity-50" : ""}`}
                    style={{
                      color: submission?.status === status.value ? status.color : "#9ca3af",
                      borderColor: submission?.status === status.value ? status.color : "transparent"
                    }}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 1. Contact Information */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-[#c4646a]" />
                Contact Information
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <InfoItem icon={Phone} label="WhatsApp / Phone" value={`+${submission?.country_code || '91'} ${submission?.phone}`} />
                <InfoItem icon={Heart} label="Couple Names" value={submission?.couple_names} />
              </div>
            </div>

            {/* 2. Wedding Date */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#c4646a]" />
                Wedding Date & Guests
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <InfoItem icon={Calendar} label="Desired Wedding Date" value={formatDate(submission?.wedding_date)} />
                <InfoItem icon={Users} label="Expected Guest Count" value={submission?.guest_count ? `${submission.guest_count} guests` : '-'} />
              </div>
            </div>

            {/* 3. Services Required */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-[#c4646a]" />
                Wedding Services Required
              </h2>
              {mainServices.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {mainServices.map((service, idx) => (
                    <span key={idx} className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                      {service}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">No services selected</p>
              )}
            </div>

            {/* 4. Service Details */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#c4646a]" />
                Service Details
              </h2>
              {Object.entries(serviceDetails).filter(([_, tags]) => tags && tags.length > 0).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(serviceDetails).filter(([_, tags]) => tags && tags.length > 0).map(([service, tags], idx) => (
                    <div key={idx} className="bg-white/5 rounded-lg p-3 border-l-2 border-[#c4646a]">
                      <p className="text-white font-medium text-sm mb-2">{service}</p>
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(tags) ? tags : []).map((tag, tidx) => (
                          <span key={tidx} className="px-2 py-0.5 bg-white/10 text-gray-300 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">No specific service details provided</p>
              )}
            </div>

            {/* 5. Property Requirements (conditional) */}
            {(submission?.property_hotel_size || submission?.property_count || submission?.property_type) && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Building className="w-5 h-5 text-[#c4646a]" />
                  Property Requirements
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {submission?.property_hotel_size && (
                    <div><p className="text-gray-400 text-sm">Hotel Size</p><p className="text-white">{submission.property_hotel_size} rooms</p></div>
                  )}
                  {submission?.property_count && (
                    <div><p className="text-gray-400 text-sm">Number of Properties</p><p className="text-white">{submission.property_count}</p></div>
                  )}
                  {propertyRoomSplit.length > 0 && (
                    <div><p className="text-gray-400 text-sm">Room Split</p><p className="text-white">{propertyRoomSplit.join(', ')}</p></div>
                  )}
                  {submission?.property_type && (
                    <div><p className="text-gray-400 text-sm">Property Type</p><p className="text-white">{submission.property_type}</p></div>
                  )}
                  {submission?.property_budget_per_night && (
                    <div><p className="text-gray-400 text-sm">Budget per Night</p><p className="text-white">{submission.property_budget_per_night}</p></div>
                  )}
                  {submission?.property_triple_occupancy && (
                    <div><p className="text-gray-400 text-sm">Triple Occupancy</p><p className="text-white">{submission.property_triple_occupancy}{submission.property_triple_count ? ` (${submission.property_triple_count} rooms)` : ''}</p></div>
                  )}
                </div>
                {submission?.property_custom_requirement && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-gray-400 text-sm">Custom Requirements</p>
                    <p className="text-white mt-1">{submission.property_custom_requirement}</p>
                  </div>
                )}
              </div>
            )}

            {/* 6. Destination */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#c4646a]" />
                Wedding Destination
              </h2>
              <p className="text-white text-lg font-medium">
                {submission?.wedding_destination || '-'}
                {submission?.wedding_destination_other ? ` — ${submission.wedding_destination_other}` : ''}
              </p>
            </div>

            {/* 7. Nuptial Events */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#c4646a]" />
                Nuptial Events
              </h2>
              {nuptialEvents.length > 0 ? (
                <>
                  <div className="flex flex-wrap gap-2">
                    {nuptialEvents.map((event, idx) => (
                      <span key={idx} className="px-3 py-1 bg-[#c4646a]/20 text-[#c4646a] rounded-full text-sm">
                        {event}
                      </span>
                    ))}
                  </div>
                  {submission?.nuptial_events_other && (
                    <p className="text-gray-400 text-sm mt-3 italic">Other: {submission.nuptial_events_other}</p>
                  )}
                </>
              ) : (
                <p className="text-gray-400">No events selected</p>
              )}
            </div>

            {/* 8. Wedding Vision & Themes */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5 text-[#c4646a]" />
                Wedding Vision & Themes
              </h2>
              {stylingThemes.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {stylingThemes.map((theme, idx) => (
                    <span key={idx} className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">
                      {theme}
                    </span>
                  ))}
                </div>
              )}
              {submission?.styling_preferences ? (
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-gray-400 text-sm mb-1">Vision Details</p>
                  <p className="text-white">{submission.styling_preferences}</p>
                </div>
              ) : (
                stylingThemes.length === 0 && <p className="text-gray-400">No wedding vision details provided</p>
              )}
            </div>

            {/* 9. Rituals & Traditions */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#c4646a]" />
                Rituals & Traditions
              </h2>
              {submission?.has_rituals === true || submission?.has_rituals === 'Yes' ? (
                <p className="text-gray-300">{submission?.ritual_details || "Yes - Details to be discussed"}</p>
              ) : (
                <p className="text-gray-400">No specific rituals mentioned</p>
              )}
            </div>

            {/* 10. Budget */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#c4646a]" />
                Estimated Budget
              </h2>
              <p className="text-[#c4646a] text-xl font-semibold">{submission?.budget_range || '-'}</p>
            </div>
          </div>

          {/* Sidebar - Notes */}
          <div className="space-y-6">
            {/* Meta Info */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-sm text-gray-400 mb-4">Submission Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Created</span>
                  <span className="text-white">{formatDate(submission?.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Template</span>
                  <span className="text-white">v{submission?.template_version || '3.0'}</span>
                </div>
              </div>
            </div>

            {/* Internal Notes */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Internal Notes
              </h3>

              {canEditStatus && (
                <form onSubmit={addNote} className="mb-6">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#c4646a] min-h-[80px] text-sm"
                  />
                  <button
                    type="submit"
                    disabled={!newNote.trim()}
                    className="mt-2 flex items-center gap-2 px-4 py-2 bg-[#c4646a] text-white rounded-lg hover:bg-[#a85459] transition-colors disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                    Add Note
                  </button>
                </form>
              )}

              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {notes.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No notes yet</p>
                ) : (
                  notes.map((note) => (
                    <div key={note.id} className="bg-white/5 rounded-lg p-4">
                      <p className="text-gray-300 text-sm">{note.note_text}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                        <span>{note.created_by_name}</span>
                        <span>•</span>
                        <span>{formatDate(note.created_at)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-[#c4646a]/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-[#c4646a]" />
      </div>
      <div>
        <p className="text-gray-400 text-sm">{label}</p>
        <p className="text-white">{value || '-'}</p>
      </div>
    </div>
  );
}

function EditField({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-sm text-gray-400 mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="luxury-input py-3 w-full"
      />
    </div>
  );
}
